import type { ConsumerApiClient } from '../client';

// ─── Types ─────────────────────────────────────────────────────

export interface SupportMessage {
  id: string;
  conversationId: string;
  senderType: 'customer' | 'agent' | 'system';
  senderId: string;
  body: string;
  attachments: Record<string, unknown>[] | null;
  isInternalNote: boolean;
  createdAt: string;
}

export interface SupportConversation {
  id: string;
  userId: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  assignedAgentId: string | null;
  subject: string;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  channel: 'email' | 'in_app' | 'phone';
  relatedOrderId: string | null;
  messages: SupportMessage[];
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
}

export interface ConversationsResponse {
  data: SupportConversation[];
  nextCursor: string | null;
}

export interface GetConversationsParams {
  status?: string;
  search?: string;
  cursor?: string;
  limit?: number;
}

export interface CreateConversationRequest {
  userId: string;
  subject: string;
  priority?: string;
  channel?: string;
  initialMessage?: string;
}

export interface SendMessageRequest {
  senderType: 'customer' | 'agent' | 'system';
  senderId: string;
  body: string;
  isInternalNote?: boolean;
}

// ─── API ───────────────────────────────────────────────────────

export interface SupportApi {
  getConversations(params?: GetConversationsParams): Promise<ConversationsResponse>;
  getConversation(id: string): Promise<SupportConversation>;
  createConversation(data: CreateConversationRequest): Promise<SupportConversation>;
  sendMessage(conversationId: string, data: SendMessageRequest): Promise<SupportMessage>;
}

/**
 * Support endpoints live at /api/v1/support/* (NOT under /mobile). This
 * module therefore uses the client's root-scoped helpers — hosts must
 * configure `rootBaseUrl` on the client for support calls to work.
 */
export function createSupportApi(client: ConsumerApiClient): SupportApi {
  return {
    getConversations(params) {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      if (params?.search) query.set('search', params.search);
      if (params?.cursor) query.set('cursor', params.cursor);
      if (params?.limit) query.set('limit', params.limit.toString());
      const qs = query.toString();
      return client.getRoot(`/support/conversations${qs ? `?${qs}` : ''}`);
    },

    getConversation(id) {
      return client.getRoot(`/support/conversations/${id}`);
    },

    createConversation(data) {
      return client.postRoot('/support/conversations', data);
    },

    sendMessage(conversationId, data) {
      return client.postRoot(`/support/conversations/${conversationId}/messages`, data);
    },
  };
}
